import {noop} from '@benchling/js-utils';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {getArchivedBannerMessage, getStepLimitWarningText} from 'analysis-tool/AnalysisToolMessages';
import {refreshAnalysisTemplate} from 'analysis-tool/polling/refreshPipeline';
import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {
  copyAnalysisTemplate,
  moveAnalysisTemplateCollection,
  unarchiveAnalysisTemplate,
} from 'analysis-tool/templates/AnalysisTemplateActions';
import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import DuplicateAnalysisTemplateModal, {
  DuplicateAnalysisTemplateModalProps,
} from 'analysis-tool/templates/DuplicateAnalysisTemplateModal';
import PublishAnalysisTemplateDropdown from 'analysis-tool/templates/PublishAnalysisTemplateDropdown';
import BaseAnalysisUIHeader from 'analysis-tool/ui/header/BaseAnalysisUIHeader';
import {AnalysisMainViewItem} from 'analysis-tool/ui/main-view/MainViewTypes';
import {BulkArchiveFilesOptions} from 'archive/ArchiveActions';
import {getArchiveAction} from 'archive/ArchiveActionsUtil';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {MoveToTemplateCollectionModal} from 'notebook/entry-templates/collections/MoveToTemplateCollectionModal';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import Badge, {BadgeColor} from 'ui-library/Badge';
import {InlineBannerActionType, InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {BreadcrumbItem} from 'ui-library/Breadcrumb';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';
import removeNulls from 'util/removeNulls';

function getOverflowMenuOptions(analysisTemplate: AnalysisTemplateGraphQL): Array<OverflowMenuOption> {
  return removeNulls([
    {
      icon: 'copy',
      contents: <span>Duplicate</span>,
      onSelect: () => {
        runModal<DuplicateAnalysisTemplateModalProps>(DuplicateAnalysisTemplateModal, {
          sourceAnalysisTemplateName: analysisTemplate.name,
          sourceAnalysisTemplateCollectionId: analysisTemplate.templateCollection.id,
          onRequestClose: noop,
          onRequestSubmit: async (params) => {
            await withLoadingScreen(
              copyAnalysisTemplate({
                name: params.name,
                copiedFromAnalysisTemplateId: analysisTemplate.id,
                templateCollectionId: params.templateCollectionId,
              })
            );
          },
        });
      },
    },
    {
      icon: 'audit-trail',
      contents: <span>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</span>,
      onSelect: () => {
        exportAuditLogs([analysisTemplate.id]);
      },
    },
    analysisTemplate.permissions.archivable && !isArchived(analysisTemplate)
      ? {
          icon: 'archive',
          contents: <span>Archive analysis template</span>,
          onSelect: () => {
            ArchiveModal.open({
              items: [analysisTemplate],
              onArchive: (
                _templateIds: Array<string>,
                purpose: string,
                options: BulkArchiveFilesOptions
              ): void => {
                getArchiveAction([analysisTemplate.id])([analysisTemplate.id], purpose, options, () => {
                  refreshAnalysisTemplate(analysisTemplate.id);
                });
              },
            });
          },
        }
      : null,
    analysisTemplate.permissions.writable && analysisTemplate.permissions.movable
      ? {
          icon: 'move',
          contents: <span>Move analysis template</span>,
          onSelect: () => {
            runModal(MoveToTemplateCollectionModal, {
              title: 'Move analysis template',
              onRequestSubmit: async (newTemplateCollectionId: string) => {
                await withLoadingScreen(
                  moveAnalysisTemplateCollection(analysisTemplate, newTemplateCollectionId)
                );
              },
              onRequestClose: noop,
              initialTemplateCollectionId: analysisTemplate.templateCollection.id,
            });
          },
        }
      : null,
  ]);
}

function getBannerStackMessages(analysisTemplate: AnalysisTemplateGraphQL): Array<InlineBannerProps> {
  const messages: Array<InlineBannerProps> = [];
  if (isArchived(analysisTemplate)) {
    messages.push({
      bannerType: InlineBannerType.WARNING,
      icon: 'box-archive',
      action: analysisTemplate.permissions.unarchivable
        ? {
            actionType: InlineBannerActionType.CUSTOM,
            actionText: 'Unarchive',
            actionOnClick: () => {
              unarchiveAnalysisTemplate(analysisTemplate.id);
            },
          }
        : undefined,
      text: getArchivedBannerMessage(analysisTemplate, analysisTemplate.archiveRecord.purpose),
      dataTestAttrs: {component: 'ArchivedBanner', element: 'root'},
    });
  } else if (analysisTemplate.steps.length >= BenchlingConfig.PIPELINES_MAX_STEP_COUNT * 0.9) {
    messages.push({
      bannerType: InlineBannerType.WARNING,
      icon: 'exclamation-triangle',
      text: getStepLimitWarningText(analysisTemplate, analysisTemplate.steps.length),
    });
  }
  return messages;
}

function getAnalysisTemplateBreadcrumbInfo(analysisTemplate: AnalysisTemplateGraphQL): {
  items: Array<BreadcrumbItem>;
  icon: BenchlingIconName;
} {
  return {
    items: [
      {
        label: analysisTemplate.templateCollection.name,
        href: TemplateCollectionDAO.collectionURL(analysisTemplate.templateCollection.id),
      },
    ],
    icon: 'template_collection',
  };
}

export interface AnalysisTemplateUIHeaderProps {
  analysisTemplate: AnalysisTemplateGraphQL;
  showOpenSidebarButton: boolean;
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void;
  onOpenRightSidebarItem?: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  onOpenSidebar: () => void;
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
}

const styles = StyleSheet.create({
  badgeWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
});

export default function AnalysisTemplateUIHeader({
  analysisTemplate,
  showOpenSidebarButton,
  onChangeSelectedItem,
  onOpenRightSidebarItem,
  onOpenSidebar,
  onOpenStepInitializationView,
}: AnalysisTemplateUIHeaderProps): JSX.Element {
  const overflowMenuOptions = getOverflowMenuOptions(analysisTemplate);
  const bannerStackMessages = getBannerStackMessages(analysisTemplate);
  const breadcrumbInfo = getAnalysisTemplateBreadcrumbInfo(analysisTemplate);

  // TODO(BNCH-148502): Allow renaming analysis templates from <AnalysisTemplateUIHeader>
  return (
    <BaseAnalysisUIHeader
      pipeline={analysisTemplate}
      showOpenSidebarButton={showOpenSidebarButton}
      onChangeSelectedItem={onChangeSelectedItem}
      onOpenRightSidebarItem={onOpenRightSidebarItem}
      onOpenSidebar={onOpenSidebar}
      onOpenStepInitializationView={onOpenStepInitializationView}
      overflowMenuOptions={overflowMenuOptions}
      bannerStackMessages={bannerStackMessages}
      breadcrumbInfo={breadcrumbInfo}
      canCreateOutputs={false}
    >
      <div className={css(styles.badgeWrapper)}>
        <Badge startingIcon={ModelTypeConstants.TEMPLATE_COLLECTION} color={BadgeColor.COLLECTIBLE_BLUE}>
          Template
        </Badge>
        <PublishAnalysisTemplateDropdown analysisTemplate={analysisTemplate} />
      </div>
    </BaseAnalysisUIHeader>
  );
}
